import React, { useEffect, useState } from 'react';
import logoFull from '../assets/epic-hoas-logo.png';
import logoIcon from '../assets/epic-hoas-icon.png';
import { useTheme } from '../Components/Custom/ThemeProvider';
import { useNavigate } from 'react-router-dom';
import {
  getToken,
  clearToken,
  clearUserName,
  clearRememberMe,
} from '../utils/TokenUtils';
import { Button } from '../Components/Catalyst/button';
import { ErrorAlert } from '../Components/Custom/ErrorAlert';
import {
  buildAuthorizationUrl,
  isWorkosConfigured,
  WORKOS_STATE_KEY,
} from '../utils/workosConfig';

/**
 * Sign in — a door, not a form.
 *
 * The email/password form, the Google button, the "forgot password" link
 * and the "remember me" checkbox all lived here until WorkOS took over
 * authentication. They are gone rather than hidden: AuthKit's hosted page
 * now owns credentials, password reset, social sign-in and the invitation
 * a homeowner accepts to set their first password.
 *
 * **There is no break-glass on this page, and that is deliberate.** The
 * manager realm keeps a `SuperAdmin`-only password path so somebody can
 * operate during a WorkOS outage. Homeowners get no equivalent: dues
 * payment is not minute-critical, and nobody is issuing backup passwords
 * to ten thousand people.
 *
 * The round trip: this page sends the browser to AuthKit, AuthKit returns
 * it to `/callback` with a single-use code, and `WorkosCallbackPage` posts
 * that code to our backend, which exchanges it and mints *our* tokens.
 */
const LoginPage: React.FC = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [error, setError] = useState<string>('');
  const [redirecting, setRedirecting] = useState<boolean>(false);

  // Someone arriving with a live session should not have to sign in
  // again. Every signed-in member lands in the same place — there is no
  // `user_type` to switch on, and the old code that asked for one always
  // returned null.
  useEffect(() => {
    const token = getToken();
    if (!token) {
      clearToken();
      clearRememberMe();
      clearUserName();
      return;
    }
    navigate('/home', { replace: true });
  }, [navigate]);

  // Leaves the app entirely: AuthKit is a hosted page, so this is a
  // full-page navigation rather than a popup or an embedded form.
  const handleSignIn = () => {
    if (!isWorkosConfigured()) {
      // Better than a button that silently does nothing. An unset client
      // ID is a deployment mistake, and the homeowner hitting it cannot
      // fix it themselves.
      setError(
        'Sign-in is not configured for this environment. Please contact support.'
      );
      return;
    }

    const { url, state } = buildAuthorizationUrl();
    // Session storage, not local: the value is meaningless after this
    // round trip and should not outlive the tab. `WorkosCallbackPage`
    // compares it against the `state` AuthKit sends back, which is what
    // makes the round trip proof against login CSRF.
    sessionStorage.setItem(WORKOS_STATE_KEY, state);
    setRedirecting(true);
    window.location.href = url;
  };

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center py-8 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img
          alt="Epic HOAs"
          src={isDarkMode ? logoIcon : logoFull}
          className="mx-auto h-[80px] w-auto"
        />
        <h2 className="mt-6 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900 dark:text-white">
          Sign in to your portal
        </h2>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
        <div className="bg-white dark:bg-gray-800 px-6 py-12 shadow sm:rounded-lg sm:px-12">
          <ErrorAlert className="mb-4">{error}</ErrorAlert>

          <Button
            type="button"
            color="sky"
            className="w-full"
            onClick={handleSignIn}
            disabled={redirecting}
          >
            {redirecting ? 'Redirecting…' : 'Sign in'}
          </Button>

          <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
            You&rsquo;ll be taken to our secure sign-in page.
          </p>

          {/*
            A homeowner who has never signed in is the common case here,
            unlike the manager portal — a member row exists from the
            moment a manager enters them, and the invitation is what turns
            it into an account. Saying so on the page saves the support
            call that "my account does not work" otherwise becomes.
          */}
          <p className="mt-2 text-center text-sm text-gray-500 dark:text-gray-400">
            New here? Use the invitation link we emailed you to set up your
            account.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
